import React, { ComponentProps, useEffect, useRef } from 'react'
import { AssetDisplayMode, AssetDisplayViewProps } from './AssetDisplay.types'
import Styles from './AssetDisplay.module.scss'
import Player from 'components/Player'
import useHero from '@onepercentio/one-ui/hooks/useHero'
import ZoomableImage from 'components/ZoomableImage/ZoomableImage'
import InfinityScroll from '@onepercentio/one-ui/components/InfinityScroll'
import usePaginationControls from '@onepercentio/one-ui/hooks/ui/usePaginationControls'
import PaginationIndicator from '@onepercentio/one-ui/components/PaginationIndicator'
import { PaginationIndicatorMode } from '@onepercentio/one-ui/components/PaginationIndicator'

type HeroImgProps = {
  src: string
  uniqueId: string
  zoomable?: boolean
}

function HeroImg({ src, uniqueId, zoomable }: HeroImgProps) {
  const { heroRef } = useHero(uniqueId, undefined, {
    onHeroDetect: (el) => {
      if (
        el.parentElement!.parentElement!.getAttribute('data-hero') ===
        heroRef.current!.parentElement!.parentElement!.getAttribute('data-hero')
      )
        return false
      const isInsideZoomable =
        !!el.parentElement?.getAttribute('data-rmiz-wrap') &&
        !!document.querySelector('[data-rmiz-overlay="true"]')
      return !isInsideZoomable
    },
  })

  return zoomable ? (
    <ZoomableImage ref={heroRef as any} src={src} className={Styles.root} />
  ) : (
    <img ref={heroRef as any} src={src} alt='' className={Styles.root} />
  )
}

/**
 * Displays the asset image in a specific manner defined by how the resource has been configured
 **/
export default function AssetDisplayView(props: AssetDisplayViewProps) {
  switch (props.mode) {
    case AssetDisplayMode.VIDEO:
      return (
        <Player
          controls={true}
          poster={props.thumbnail}
          src={props.src}
          className={Styles.root}
        />
      )
    case AssetDisplayMode.IMAGE:
      return (
        <HeroImg
          src={props.src}
          uniqueId={String(props.uniqueId)}
          zoomable={props.zoomable}
        />
      )
    case AssetDisplayMode.ANIMATION:
      return (
        <video
          controls={false}
          autoPlay
          playsInline
          muted
          loop
          src={props.src}
          className={Styles.root}
        />
      )
  }
  return <></>
}

function CarouselItemWrapper(
  main: ComponentProps<typeof AssetDisplayView> & { customProportion: boolean }
) {
  return (
    <div
      className={main.customProportion ? Styles.carouselItem : undefined}
      style={{ backgroundImage: `url(${main.src})` }}>
      <div>
        <AssetDisplayView {...main} />
      </div>
    </div>
  )
}

export function Carousel({
  main,
  extra,
}: {
  main: ComponentProps<typeof AssetDisplayView>
  extra: (ComponentProps<typeof AssetDisplayView> & {
    customProportion: boolean
  })[]
}) {
  const ref = useRef<HTMLDivElement>(null)
  const { controls } = usePaginationControls(ref, { snapToPage: true })
  useEffect(() => {
    const el = ref.current!
    el.style.height = `${el.clientHeight}px`
    el.addEventListener('touchmove', (e) => e.preventDefault())
    el.addEventListener('wheel', (e) => {
      if (e.deltaX) e.preventDefault()
    })
    el.style.overflow = 'hidden'
  }, [])
  return (
    <>
      <div className={Styles.carousel}>
        <InfinityScroll
          ref={ref}
          pageSize={1}
          items={[
            <AssetDisplayView {...main} />,
            ...extra.map((a) => <CarouselItemWrapper {...a} />),
          ]}
          className={Styles.infinity}
        />
        {controls}
        <PaginationIndicator
          className={Styles.pagination}
          scrollableRef={ref}
          size={12}
          infinite
        />
      </div>
    </>
  )
}
